import {
  PropsWithChildren,
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
  useReducer,
  useMemo,
  useEffect,
} from "react";
import { ContentCategory } from "types/appConfigV2";
import { SearchActionTypes, searchReducer, SearchState } from "./search";

type IProps = PropsWithChildren<{
  initialCategory: ContentCategory;
}>;

export type ContentView = "grid" | "list" | "masonry";
export type GridSize = "small" | "medium" | "large";

const FilterContext = createContext<{
  contentView: ContentView;
  setContentView: Dispatch<SetStateAction<ContentView>>;
  searchState: SearchState;
  searchDispatch: Dispatch<SearchActionTypes>;
  gridSize: GridSize;
  setGridSize: Dispatch<SetStateAction<GridSize>>;
}>(undefined!);

export const FilterProvider = ({ initialCategory, children }: IProps) => {
  const getStoredContentView = (): ContentView => {
    const stored = localStorage.getItem('connector-content-view');
    return (stored as ContentView) || "grid";
  };

  const getStoredGridSize = (): GridSize => {
    const stored = localStorage.getItem('connector-grid-size');
    return (stored as GridSize) || "medium";
  };

  const [contentView, setContentView] = useState<ContentView>(getStoredContentView());
  const [gridSize, setGridSize] = useState<GridSize>(getStoredGridSize());
  let initialSearchModel = initialCategory.filter || {};
  initialSearchModel = Object.assign(
    initialSearchModel,
    { deploymentSiteIds: [initialCategory.deploymentSiteId] },
    initialCategory.rootFolderId
      ? { folderIds: [initialCategory.rootFolderId] }
      : null
  );

  // @ts-ignore
  const [searchState, searchDispatch] = useReducer(searchReducer, {
    search: initialSearchModel,
    defaultDeploymentSiteId: initialCategory.deploymentSiteId,
    currentDeploymentSiteId: initialCategory.deploymentSiteId,
    imageTemplateId: initialCategory.imageTemplateId,
    currentCategory: initialCategory,
    currentFolderId: initialCategory.rootFolderId,
    activeFilters: [],
  });

  const searchMemo = useMemo(() => {
    return { searchState, searchDispatch };
  }, [searchState, searchDispatch]);

  useEffect(() => {
    localStorage.setItem('connector-content-view', contentView);
  }, [contentView]);

  useEffect(() => {
    localStorage.setItem('connector-grid-size', gridSize);
  }, [gridSize]);

  return (
    <FilterContext.Provider
      value={{
        contentView,
        setContentView,
        gridSize,
        setGridSize,
        searchState: searchMemo.searchState,
        searchDispatch: searchMemo.searchDispatch,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = () => {
  let context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
};
